import { useCallback, useEffect, useState } from 'react';

import { Flex, Loading, Page, Typography } from 'components';

import {
  ActivationType,
  AuthenticatorInstall,
  AuthenticatorProblem,
  Confirmation,
  Qrcode,
  TokenAuthneticator,
  TokenConfimation,
  TokenDisable,
  TokenRecovery,
} from 'features/mfa';

import { createUrl, formatDate, request } from 'app/utils';
import { useNavigate } from 'react-router-dom';
import { useSchool } from 'features/school';
import { AuthApiResponse, useAuth } from 'features/auth';
import usestorage from 'app/utils/storage';
import { AxiosError } from 'axios';
import { useAlert } from 'features/alert';
import { useCookies } from 'react-cookie';

interface ActivationProps {
  action?: 'login' | 'activation' | 'disable';
  type?: 'login' | 'config';
}
const Activation = ({ action = 'activation', type = 'config' }: ActivationProps) => {
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [activationType, setActivationType] = useState<ActivationType>(action === 'activation' ? 'authenticatorInstall' : 'tokenAuthneticator');
  const { apiGatewayUrl } = useSchool();
  const [qrcode, setQrcode] = useState<string>();
  const navigate = useNavigate();
  const { setAuth, redirectUrl, setRedirectUrl } = useAuth();
  const storage = usestorage('sessionStorage');
  const { setAlert, alert } = useAlert();
  const { REACT_APP_UNSECURE_COOKIE } = process.env;
  const [, setCookie] = useCookies(['token']);
  const [recoveryCode, setRecoveryCode] = useState<string>('');

  const handleAuthenticatorInstall = useCallback(() => {
    request({
      url: `/api/v1/User/MFA/SendActivationOTP`,
      method: 'post',
      baseURL: apiGatewayUrl,
    })
      .then(({ data }) => {
        setLoadingEdit(false);
        setActivationType('tokenConfimation');
      })
      .catch((error: AxiosError) => {
        setAlert({
          type: 'error',
          message: `Ocorreu um erro inesperado, por favor tente novamente.`,
        });
        setLoadingEdit(false);
      });
  }, []);

  const handleTokenConfimation = useCallback((value: String) => {
    request({
      url: `/api/v1/User/MFA/DisplayQRCodeTOTP?format=base64`,
      method: 'post',
      baseURL: apiGatewayUrl,
      data: {
        mfaCode: value,
      },
    })
      .then(({ data }) => {
        setLoadingEdit(false);

        setQrcode(data);

        setActivationType('qrcode');
      })
      .catch((error: AxiosError) => {
        setAlert({
          type: 'error',
          message: `Verifique se o código foi digitado corretamente.`,
        });
        setLoadingEdit(false);
      });
  }, []);

  const handleQrcode = useCallback(() => {
    setActivationType('tokenAuthneticator');
  }, []);

  const handleTokenAuthneticator = useCallback((value: String) => {
    request<AuthApiResponse>({
      url: `/api/v1/User/MFA/Authentication`,
      method: 'post',
      baseURL: apiGatewayUrl,
      data: {
        mfaCode: value,
      },
    })
      .then(({ data }) => {
        setLoadingEdit(false);
        setAuth(data);
        const { token, fullName, customerUuid, roles, loginEmail } = data;

        const roleIsStudent = roles.find((role) => role.name === 'Student');

        if (roleIsStudent || !roleIsStudent) {
          let goTo = redirectUrl;

          const storageRedirectUrl = storage.get('redirect_url');
          if (storageRedirectUrl) {
            goTo = storageRedirectUrl;
            storage.remove('redirect_url');
          }

          const enrollSlug = storage.get('enrollSlug');
          if (enrollSlug) {
            const enrollProductPrefix = storage.get('enrollProductPrefix');
            goTo = `/${enrollProductPrefix || 'products'}/${enrollSlug}`;

            storage.remove('enrollSlug');
            storage.remove('enrollProductPrefix');
          }

          setRedirectUrl(null);

          const unsecureCookie = REACT_APP_UNSECURE_COOKIE === 'true' || window.location.hostname === 'localhost';

          setCookie('token', token.token, {
            path: '/',
            domain: window.location.hostname,
            expires: formatDate(token.expiresAt).toDate(),
            secure: !unsecureCookie,
          });

          // Sentry.setUser({ id: studentUuid, email });

          if (window.octadesk) {
            window.octadesk.chat.login({
              user: {
                name: fullName,
                email: loginEmail,
              },
              customFields: [
                {
                  id: 'customerUuid',
                  value: customerUuid,
                },
              ],
            });
          }

          setAuth(data);

          if (action === 'activation') {
            getRecoveryCode();
          } else {
            navigate(goTo || '/');
          }
        }
      })
      .catch((error: AxiosError) => {
        setAlert({
          type: 'error',
          message: `Verifique se o código foi digitado corretamente.`,
        });
        setLoadingEdit(false);
      });
  }, []);

  const getRecoveryCode = useCallback(() => {
    request({
      url: `/api/v1/User/MFA/GetRecoveryCode`,
      method: 'get',
      baseURL: apiGatewayUrl,
    })
      .then(({ data }) => {
        setLoadingEdit(false);
        setRecoveryCode(data.apiResultData);
        setActivationType('confirmation');
      })
      .catch((error: AxiosError) => {
        setAlert({
          type: 'error',
          message: `Ocorreu um erro inesperado, por favor tente novamente.`,
        });
        setLoadingEdit(false);
      });
  }, []);

  const handleTokenAuthenticatorProblem = useCallback(() => {
    setActivationType('authenticationProblemRecovery');
  }, []);

  const handleTokenAuthenticatorProblemRecovery = useCallback((value) => {
    request({
      url: `/api/v1/User/MFA/ResetUsingRecoveryCode`,
      method: 'post',
      baseURL: apiGatewayUrl,
      data: {
        mfaCode: value,
      },
    })
      .then(({ data }) => {
        setAuth(data);

        const storageRedirectUrl = storage.get('redirect_url');
        let goTo = redirectUrl;

        if (storageRedirectUrl) {
          if (storageRedirectUrl !== '/login') {
            goTo = storageRedirectUrl;
          }
          storage.remove('redirect_url');
        }
        if (action === 'activation') {
          getRecoveryCode();
        } else {
          navigate(goTo || '/');
        }
        setLoadingEdit(false);
      })
      .catch((error: AxiosError) => {
        setAlert({
          type: 'error',
          message: `Ocorreu um erro inesperado, por favor tente novamente.`,
        });
        setLoadingEdit(false);
      });
  }, []);

  const handleTokenDisable = useCallback((value: string) => {
    request({
      url: `/api/v1/User/MFA/DisableUsingRecoveryCode`,
      method: 'post',
      baseURL: apiGatewayUrl,
      data: {
        mfaCode: value,
      },
    })
      .then(({ data }) => {
        navigate(createUrl('account'));
      })
      .catch((error: AxiosError) => {
        setAlert({
          type: 'error',
          message: `Verifique se o código foi digitado corretamente.`,
        });
        setLoadingEdit(false);
      });
  }, []);

  useEffect(() => {
    if (action === 'disable') {
      setActivationType('tokenDisable');
    }
  }, []);

  const handleConfirmation = useCallback(() => {
    const storageRedirectUrl = storage.get('redirect_url');
    let goTo = redirectUrl;

    if (storageRedirectUrl) {
      if (storageRedirectUrl !== '/login') {
        goTo = storageRedirectUrl;
      }
      storage.remove('redirect_url');
    }
    if (action === 'activation') {
      navigate(goTo || '/');
    } else {
      navigate('/mfa');
    }
  }, []);

  return (
    <>
      {loadingEdit && <Loading alpha />}

      <Page title="Autenticação de dois fatores">
        <Flex
          bg={action === 'login' || type === 'login' ? 'authBoxBackground' : 'authBoxBackground'}
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          padding={action === 'login' ? 0 : 50}
          marginLeft={action === 'login' ? '-6%' : 0}
        >
          {!!(alert.type && alert.message) && (
            <Flex
              bg={alert.type === 'error' ? 'alert_400' : 'primary_400'}
              borderRadius={4}
              p={2}
              mb={4}
              maxWidth={600}
              maxHeight={40}
              alignItems="center"
              justifyContent="center"
            >
              <Typography color="white" type="type2" fontWeight="bold">
                {alert.message}
              </Typography>
            </Flex>
          )}
          {activationType === 'authenticatorInstall' && <AuthenticatorInstall onClick={() => handleAuthenticatorInstall()} />}
          {activationType === 'tokenConfimation' && (
            <TokenConfimation onClick={(value) => handleTokenConfimation(value)} onClickLink={() => handleAuthenticatorInstall()} />
          )}
          {activationType === 'qrcode' && <Qrcode onClick={() => handleQrcode()} qrcode={qrcode} />}
          {activationType === 'tokenAuthneticator' && (
            <TokenAuthneticator
              onClick={(value) => {
                handleTokenAuthneticator(value);
              }}
              onClickLink={() => handleTokenAuthenticatorProblem()}
              isLogin={action === 'activation' ? false : true}
            />
          )}
          {activationType === 'confirmation' && <Confirmation recoveryCode={recoveryCode} onClick={() => handleConfirmation()} />}

          {activationType === 'authenticationProblem' && (
            <AuthenticatorProblem onClick={() => navigate(createUrl('login'))} onClickLink={() => handleTokenAuthenticatorProblem()} />
          )}

          {activationType === 'authenticationProblemRecovery' && (
            <TokenRecovery onClick={(value) => handleTokenAuthenticatorProblemRecovery(value)} />
          )}
          {activationType === 'tokenDisable' && <TokenDisable onClick={(value) => handleTokenDisable(value)} />}
        </Flex>
      </Page>
    </>
  );
};

export default Activation;
